import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { AuthConfigResponse, LoginResponse, UserProfile } from '@regigov/contracts';
import { api, getToken, setToken } from '../api/client';

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  user: UserProfile | null;
  config: AuthConfigResponse | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Start using a new sign-in issued by the API (the testing identity switch), exactly as a login does. */
  adopt: (res: LoginResponse) => void;
  /** Re-reads the profile; used after actions that change roles or participant linkage. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Cached queries belong to the signed-in user; drop them whenever the user changes.
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [user, setUser] = useState<UserProfile | null>(null);
  const [config, setConfig] = useState<AuthConfigResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api('/auth/config', AuthConfigResponse);
        if (!cancelled) setConfig(cfg);
      } catch {
        /* API unavailable; login page shows the error */
      }
      if (!getToken()) {
        if (!cancelled) setStatus('anonymous');
        return;
      }
      try {
        const me = await api('/auth/me', UserProfile);
        if (!cancelled) {
          setUser(me);
          setStatus('authenticated');
        }
      } catch {
        setToken(null);
        if (!cancelled) setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const adopt = useCallback((res: LoginResponse) => {
    setToken(res.accessToken);
    queryClient.clear();
    setUser(res.user);
    setStatus('authenticated');
  }, [queryClient]);

  const login = useCallback(async (username: string, password: string) => {
    adopt(await api('/auth/login', LoginResponse, { method: 'POST', body: JSON.stringify({ username, password }) }));
  }, [adopt]);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', z.undefined(), { method: 'POST' });
    } finally {
      setToken(null);
      queryClient.clear();
      setUser(null);
      setStatus('anonymous');
    }
  }, [queryClient]);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    setUser(await api('/auth/me', UserProfile));
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, user, config, login, logout, adopt, refresh }),
    [status, user, config, login, logout, adopt, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
